import { styled } from '@linaria/react';
import { useDisclosure } from '@mantine/hooks';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AgentInput } from './components/chat/agent-input';
import { AgentMessageList } from './components/chat/agent-message-list';
// The agent chat components read `--flora-*` variables whose light defaults live
// here. Declared explicitly rather than relying on navigation.tsx happening to
// import the flora-agent route eagerly.
import '../flora-agent/flora-agent-dark-theme.css';
import { FloraAsideHeader } from './components/flora-aside-header';
import { FloraAsideEmptyState } from './components/flora-aside-empty-state/flora-aside-empty-state';
import { FloraChatHistoryDropdown } from './components/flora-chat-history-dropdown/flora-chat-history-dropdown';
import { FloraContextBanner } from './components/flora-context-banner/flora-context-banner';
import { deriveTodoBarState } from './components/flora-todo-bar/flora-todo-bar.helpers';
import { FloraResizeHandle } from './components/flora-resize-handle';
import { type FloraAsideChat, useFloraAsideChat } from './hooks/use-flora-aside-chat';
import { isNewChatShortcut } from './flora-aside.helpers';
import { useFloraAsideSession, useHasFloraAsideSession } from './flora-aside-session.context';
import { useFlora } from './flora.context';
import { FLORA_ASIDE_MIN_WIDTH } from './flora.type';

interface FloraAsideProps {
  onClose?: () => void;
  chatId?: string | null;
  /**
   * Renders the panel flush (no rounded corners/shadow) and hides the built-in
   * resize handle. Used when the aside is docked somewhere other than the
   * right-hand app shell — e.g. the left pane of the Flora page viewer — where
   * the right-edge-anchored resize math does not apply.
   */
  isPageView?: boolean;
  /**
   * Optional context prepended to the FIRST message of a fresh thread only,
   * making Flora aware of the surrounding surface (e.g. the published page the
   * user is viewing). The displayed user text is left untouched; the preamble
   * is sent to the agent but never shown in the transcript. Only needed by
   * callers (like the page viewer) that mount their own `FloraAside` instance
   * outside `FloraContext`'s reach — the standard right-dock instance falls
   * back to `contextPreamble` from `useFlora()` when this prop is omitted.
   */
  contextPreamble?: string;
  /**
   * Human-readable label for the surrounding surface (e.g. the page title),
   * shown in the context bar so the user can see what Flora is aware of. Same
   * prop-or-context fallback as `contextPreamble`.
   */
  contextLabel?: string;
}

/**
 * Docked Flora chat panel — header (whose title opens a chat-history dropdown),
 * message list or empty state, context banner, and input. Runs the same chat session and components as the
 * full-page Flora chat; the context banner (and its first-turn preamble) is the
 * only chat difference. The right-dock instance shows the conversation kept by
 * `FloraAsideSessionProvider`, so it survives page changes and closing the
 * panel, and after a refresh it reconnects to a turn still running or waiting
 * on a question. The page-view instance (and any instance rendered outside
 * the provider) runs a conversation of its own.
 *
 * @param {() => void} [onClose] - Invoked when the header close button is pressed.
 * @param {string | null} [chatId] - Thread id to open; empty starts a new chat.
 * @param {boolean} [isPageView] - Flush styling + hides the resize handle for non-right docks.
 * @param {string} [contextPreamble] - Context prefixed to the first outgoing message only.
 * @param {string} [contextLabel] - Surface label shown in the context bar (e.g. the page title).
 * @returns {JSX.Element} The docked Flora chat panel.
 */
export const FloraAside = (props: FloraAsideProps) => {
  const hasSharedSession = useHasFloraAsideSession();
  if (props.isPageView || props.chatId || !hasSharedSession) {
    return <LocalFloraAside {...props} />;
  }
  return <SharedFloraAside {...props} />;
};

/**
 * The right dock, showing the provider's persistent conversation.
 *
 * @param {FloraAsideProps} props - The panel props
 * @returns {JSX.Element} The panel
 */
const SharedFloraAside = (props: FloraAsideProps) => {
  const chat = useFloraAsideSession();
  // The conversation publishes right after it starts; hold the frame until then.
  if (!chat) return <FloraAsideContainer isPageView={false} />;
  return <FloraAsideView {...props} chat={chat} />;
};

/**
 * A panel with a conversation of its own, which ends when the panel unmounts.
 *
 * @param {FloraAsideProps} props - The panel props
 * @returns {JSX.Element} The panel
 */
const LocalFloraAside = (props: FloraAsideProps) => {
  const chat = useFloraAsideChat({
    chatId: props.chatId,
    isPageView: props.isPageView,
    contextPreamble: props.contextPreamble,
  });
  return <FloraAsideView {...props} chat={chat} />;
};

/**
 * Renders a side-panel conversation.
 *
 * @param {FloraAsideProps & { chat: FloraAsideChat }} props - The panel props and the conversation to show
 * @returns {JSX.Element} The panel
 */
const FloraAsideView = ({
  onClose,
  isPageView = false,
  contextLabel,
  chat,
}: FloraAsideProps & { chat: FloraAsideChat }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isHistoryOpen, { open: openHistory, close: closeHistory }] = useDisclosure(false);
  const {
    contextualInfo,
    contextLabel: contextLabelFromContext,
    floraWidth,
    floraMaxWidth,
    isResizingFlora,
    startFloraResize,
    setFloraWidth,
  } = useFlora();
  // The page viewer mounts its own FloraAside outside FloraContext's reach and
  // passes these explicitly; the standard right-dock instance seeds them via
  // FloraContext instead (e.g. from session-detail.tsx).
  const resolvedContextLabel = contextLabel ?? contextLabelFromContext;

  const {
    messages,
    questions,
    threadId,
    chatHistories,
    question,
    setQuestion,
    isActiveQuestion,
    isWaitingForFirstResponse,
    loadingMessage,
    tokenUsage,
    latestTurnIsLive,
    attachments,
    submitQuestion,
    handleNewChat,
    handleDeleteHistory,
    handleEditHistory,
    handleAnswerSubmit,
    stopAgent,
    handleToolApproval,
    fetchChatHistories,
    selectedChatId,
    setSelectedChatId,
    selectedChatHistory,
    isLoadingHistories,
    isContextExpanded,
    toggleContext,
  } = chat;

  // The current turn's `write_todos` plan, docked atop the composer.
  const todoBar = useMemo(() => deriveTodoBarState(messages, questions), [messages, questions]);

  // The session fetches histories on mount and when a chat is opened; also
  // refresh whenever the overlay opens so the list is current.
  useEffect(() => {
    if (!isHistoryOpen) return;
    const cleanup = fetchChatHistories();
    if (cleanup) return cleanup;
  }, [isHistoryOpen, fetchChatHistories]);

  // Follow-up chips send like a typed message, same as the full page.
  const handleFollowupClick = useCallback(
    (prompt: string) => {
      submitQuestion(prompt);
    },
    [submitQuestion],
  );

  // Empty-state chips send like a typed message, but — like the full-page
  // landing — never start a turn mid-answer or while a file is uploading.
  const handlePromptSelect = useCallback(
    (prompt: string) => {
      if (isActiveQuestion || attachments.hasUploading) return;
      submitQuestion(prompt);
    },
    [isActiveQuestion, attachments.hasUploading, submitQuestion],
  );

  const isChatEmpty = questions.length === 0 && !isWaitingForFirstResponse;
  // Parked on a question or approval, the turn is waiting on the user, not on
  // Flora: history, New chat and the full page stay available (leaving
  // detaches; the parked job waits and replays its card on return).
  const lastMessage = messages[messages.length - 1];
  const isAwaitingUser = Boolean(lastMessage?.pendingQuestions?.length || lastMessage?.pendingToolApproval);
  const isFloraWorking = isActiveQuestion && !isAwaitingUser;
  // A selected chat (e.g. restored on refresh) is empty only until its history
  // loads — don't flash the greeting, or offer chips that would send into it.
  const showEmptyState = !isPageView && isChatEmpty && !selectedChatId;

  // The header's new-chat button and a turn starting (e.g. sent from the
  // composer below the dropdown) both close history.
  const handleHeaderNewChat = useCallback(() => {
    closeHistory();
    handleNewChat();
  }, [closeHistory, handleNewChat]);
  useEffect(() => {
    if (isActiveQuestion) closeHistory();
  }, [isActiveQuestion, closeHistory]);

  // ⌘⇧O starts a new chat from the right dock. The full Flora page binds the
  // same keys itself, so stay out of its way there to avoid a double reset.
  const isOnFullFloraPage = pathname.startsWith('/application/flora-agent');
  useEffect(() => {
    if (isPageView || isOnFullFloraPage) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isNewChatShortcut(event)) return;
      event.preventDefault();
      if (isFloraWorking) return;
      handleHeaderNewChat();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPageView, isOnFullFloraPage, isFloraWorking, handleHeaderNewChat]);

  const handleOpenInFullPage = () => {
    onClose?.();
    if (threadId) {
      navigate(`/application/flora-agent/${threadId}`);
    } else {
      navigate('/application/flora-agent');
    }
  };

  return (
    <FloraAsideContainer isPageView={isPageView}>
      {!isPageView && (
        <FloraResizeHandle
          onMouseDown={startFloraResize}
          isDragging={isResizingFlora}
          onResize={(deltaX) => setFloraWidth((previousWidth) => previousWidth + deltaX)}
          width={floraWidth}
          minWidth={FLORA_ASIDE_MIN_WIDTH}
          maxWidth={floraMaxWidth}
        />
      )}
      <FloraAsideHeader
        isPageView={isPageView}
        selectedChatHistory={selectedChatHistory}
        isActiveQuestion={isFloraWorking}
        isHistoryOpen={isHistoryOpen}
        isChatEmpty={isChatEmpty}
        onClose={onClose}
        onNewChat={handleHeaderNewChat}
        onOpenHistory={openHistory}
        onCloseHistory={closeHistory}
        onOpenInFullPage={isPageView ? undefined : handleOpenInFullPage}
      />
      <FloraAsideBody>
        <AgentMessageList
          questions={questions}
          messages={messages}
          isWaitingForFirstResponse={isWaitingForFirstResponse}
          isActiveQuestion={isActiveQuestion}
          loadingMessage={loadingMessage}
          chatHistoryId={threadId}
          latestTurnIsLive={latestTurnIsLive}
          enableTurnRail={false}
          onFollowupClick={handleFollowupClick}
          onAnswerSubmit={handleAnswerSubmit}
          onToolApproval={handleToolApproval}
        />
        {showEmptyState && <FloraAsideEmptyState onPromptSelect={handlePromptSelect} />}
        <FloraChatHistoryDropdown
          isOpen={isHistoryOpen}
          chatHistories={chatHistories}
          activeChatId={threadId}
          isLoading={isLoadingHistories}
          onClose={closeHistory}
          onSetThreadId={setSelectedChatId}
          onNewChat={handleNewChat}
          onDeleteHistory={handleDeleteHistory}
          onEditHistory={handleEditHistory}
        />
      </FloraAsideBody>
      {((contextualInfo &&
        (contextualInfo.currentObjects.length > 0 ||
          (contextualInfo.startDate && contextualInfo.endDate) ||
          (contextualInfo.selectedMeasures?.length ?? 0) > 0)) ||
        resolvedContextLabel) && (
        <FloraContextBanner
          contextualInfo={contextualInfo}
          pageTitle={resolvedContextLabel}
          isExpanded={isContextExpanded}
          onToggle={toggleContext}
        />
      )}
      <AgentInput
        question={question}
        onQuestionChange={setQuestion}
        onSubmit={submitQuestion}
        isActiveQuestion={isActiveQuestion}
        tokenUsage={tokenUsage}
        attachments={attachments}
        onStop={stopAgent}
        threadId={threadId}
        todos={todoBar.todos}
        todosLive={isActiveQuestion && todoBar.isCurrentTurn}
        disclaimer="Flora can make mistakes. Verify important info."
      />
    </FloraAsideContainer>
  );
};

const FloraAsideContainer = styled.div<{ isPageView?: boolean }>`
  /* Narrower side gutter than the full page for the shared chat components. */
  --flora-chat-gutter: 16px;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
  border-radius: ${(props) => (props.isPageView ? '0' : '12px')};
  overflow: hidden;
  box-shadow: ${(props) => (props.isPageView ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.05)')};
  position: relative;
`;

/**
 * Message area: hosts the list, the empty state over it, and the history
 * dropdown. Its own stacking context keeps the dropdown's layers below the
 * header (which the card tucks under) and the resize handle.
 */
const FloraAsideBody = styled.div`
  position: relative;
  z-index: 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;
